import { Button, Grid, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { API } from 'core/requests';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { useCompany } from 'contexts';
import { DialogTeleport, MoneyInput } from 'components';
import { LoadingButton } from '@mui/lab';
import { useCallback, useEffect, useState } from 'react';
import { get } from 'lodash';
import { budgetLoadingInitialState } from 'constants/budget';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

type LoadingTypes = {
	addAmountLoading: boolean;
};

const ManageAddBankAccount = ({ openAddBank, setOpenAddBank, stripePromise, getCurrentTransaction, walletMoney, setWalletMoney }) => {

    const { enqueueSnackbar } = useSnackbar()
    const { company }: any = useCompany()
    const [showBank, setShowBank] = useState<any>(false);
    const [bankAccounts, setBankAccounts] = useState<any>({});
    const [openAddAmount, setOpenAddAmount] = useState(false);
		const [accountId, setAccountId] = useState<number>(0);
    const [loading, setLoading] = useState<LoadingTypes>(budgetLoadingInitialState);
		const [deletingAccountId, setDeletingAccountId] = useState<any>(null);

		const ach_fee_type: any = company?.ach_fee_type;
		const ach_fee: any = company?.ach_fee;
		let fee = 0;
	
		let totalAmount = parseFloat(walletMoney);
		if (ach_fee_type === 'percentage') {
			fee = (parseFloat(walletMoney) * parseFloat(ach_fee)) / 100;
			totalAmount = parseFloat(walletMoney) + fee;
		} else if (ach_fee_type === 'fixed') {
			fee = parseFloat(ach_fee);
			totalAmount = parseFloat(walletMoney) + parseFloat(ach_fee);
		} 

		const [totalWalletMoney, setTotalWalletMoney] = useState<number>(totalAmount);

    const [achClientSecret, setAchClientSecret] = useState<string | null>(null);
    const [achSessionId, setAchSessionId] = useState<string | null>(null);
    const [achSetupCompleted, setAchSetupCompleted] = useState<boolean>(false);

    const { mutate: refetchBankAccounts } = useMutation(() => API.get('getBankAccountDetails'), {
			onSuccess: (response: any) => { !response?.errors && setBankAccounts(response) }
    })

    const { mutate: createAchSetupSession } = useMutation(
      () => API.create('createAchSetupSession', {}),
      {
        onSuccess: (response: any) => {
          if (response?.errors || !response?.client_secret) {
            enqueueSnackbar(response?.message || 'Could not start bank verification.', { variant: 'error' });
            setOpenAddBank(false);
            return;
          }
          setAchClientSecret(response.client_secret);
          setAchSessionId(response.session_id);
        },
        onError: () => {
          enqueueSnackbar('Could not start bank verification.', { variant: 'error' });
          setOpenAddBank(false);
        }
      }
    );

    const { mutate: confirmAchSetup, isLoading: isConfirmingAch } = useMutation(
      (sessionId: string) => API.create('confirmAchSetup', { session_id: sessionId }),
      {
        onSuccess: (response: any) => {
          if (response?.errors || response?.status !== 'success') {
            enqueueSnackbar(response?.message || 'Bank verification did not complete.', { variant: 'error' });
            return;
          }
          enqueueSnackbar('Bank account added successfully.', { variant: 'success' });
          refetchBankAccounts();
          setAchClientSecret(null);
          setAchSessionId(null);
          setOpenAddBank(false);
        }
      }
    );

    const handleAchCheckoutComplete = useCallback(() => {
      setAchSetupCompleted(true);
      if (achSessionId) confirmAchSetup(achSessionId);
    }, [achSessionId, confirmAchSetup]);

    const { mutate: chargeBankAccount } = useMutation((payload: any) => {
			return API.create('chargeBankAccount', payload);
		}, {
			onSuccess: (response: any) => {
				if (!response?.errors) {
					const successStatuses = ['succeeded', 'pending', 'processing', 'requires_action'];
					enqueueSnackbar(response?.message,
						{
							variant: successStatuses.includes(response?.status) ? 'success' : 'error',
						},
					);

					getCurrentTransaction();
					refetchBankAccounts();
					setWalletMoney(1.0);
					setTotalWalletMoney(totalAmount);
					setLoading(budgetLoadingInitialState);
					setOpenAddAmount(false);
				} else setLoading(budgetLoadingInitialState);
			},
    })

    const deleteBankAccountMutation = useMutation(
			(payload: any) => API.delete('removeBankAccount', null,
				{
					queryParams: { company_id: company?.id },
					body: payload
				}),
			{
				onSettled: (response: any) => {
					if (!response.errors) {
						setBankAccounts({});
						enqueueSnackbar('Bank account removed successfully.',
							{
								variant: 'success',
							});
						refetchBankAccounts();
						addAmountForm.setValues({
							...addAmountForm.values, 
							total_amount: totalWalletMoney.toFixed(2),
						})
						setDeletingAccountId(null);
					}
				},
			},
    )

    const addAmountForm = useFormik({
			initialValues: {
				amount: "0",
				fee_type: '',
				fee_value: 0,
				total_amount: "0",
				fee_amount: "0",
			},
			validationSchema: yup.object({
				amount: yup.number().min(1).required("This field is Required"),
			}),
			onSubmit: async (values, { resetForm }) => {
				setLoading({
					...loading,
					addAmountLoading: true
				});

				await chargeBankAccount({
					amount: parseFloat(values?.amount),
					payment_method_id: (bankAccounts?.bank_account?.data?.length) && bankAccounts?.bank_account?.data[accountId].id,
					currency: "usd",
					fee_type: values?.fee_type,
					fee_value: values?.fee_value,
					total_amount: parseFloat(values?.total_amount || "0"),
					fee_amount: parseFloat(values?.fee_amount || "0"),
				})

				// Then reset form
				resetForm();
			},
    });

    useEffect(() => {
			setTotalWalletMoney(totalAmount);

			addAmountForm.setValues({ 
				amount: parseFloat(walletMoney).toFixed(2),
				fee_type: ach_fee_type,
				fee_value: ach_fee,
				total_amount: totalAmount.toFixed(2),
				fee_amount: fee.toFixed(2)
			})

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalAmount])
		
    useEffect(() => refetchBankAccounts(), [refetchBankAccounts]);

    useEffect(() => {
      if (openAddBank && !achClientSecret) {
        createAchSetupSession();
      }
      if (!openAddBank) {
        setAchClientSecret(null);
        setAchSessionId(null);
        setAchSetupCompleted(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openAddBank]);

    useEffect(() => {
			if (get(bankAccounts, "bank_account.data") && get(bankAccounts, "bank_account.data").length) {
				setShowBank(true)
			} else setShowBank(false)
    }, [bankAccounts])

    return (
			<>
				<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} my={1}>
					<Typography variant='h6' component='h6' fontWeight={500}><b>Bank Accounts</b></Typography>
				</Box>

				{(!showBank) && (
					<Box sx={{ textAlign: "center", marginBottom: 3, border: '1px solid #607d8b', borderRadius: 3, p: 3 }}>
						<Typography variant='body2' component='span' fontWeight={500}>No Bank Accounts Found</Typography>
					</Box>
				)}

				{
					showBank && bankAccounts?.bank_account?.data.map((account: any, idx: number) => {
						return <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={3} sx={{ border: '1px solid #607d8b', borderRadius: 3, p: 1 }} key={account?.id}>
							<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
								<Typography variant='h6' color={'primary'} component='h6' fontWeight={700} style={{ marginRight: 7 }}>{account?.bank_name}</Typography>
								<Typography variant='body2' component='span' color={'info'} fontWeight={500}> **** {account?.last4} </Typography>
							</Box>
							<Box>
								{
									account?.status === 'verified' && (
										<Button variant="contained" onClick={() => {
											setOpenAddAmount(true);
											setAccountId(idx);
											setTotalWalletMoney(totalAmount);
											addAmountForm.setValues({ 
												...addAmountForm.values, 
												amount: parseFloat(walletMoney).toFixed(2),
												fee_type: ach_fee_type,
												fee_value: ach_fee,
												total_amount: totalAmount.toFixed(2),
												fee_amount: fee.toFixed(2)  
											});
										}}>Add Funds</Button>
									)
								}
								
								<LoadingButton
									type="button"
									loading={account?.id === deletingAccountId}
									autoFocus
									variant='text'
									color={'primary'}
									onClick={() => {
										setDeletingAccountId(account?.id);
										deleteBankAccountMutation.mutate({
											id: account?.id
										})
									}}
								>
									<ClearIcon />
									<span>Delete</span>
								</LoadingButton>
							</Box>
						</Box>
					})
				}

				{/* Add bank account dialog — Stripe Embedded Checkout with Financial Connections */}
				<DialogTeleport
					dialogOpen={openAddBank}
					handleDialogClose={() => setOpenAddBank(false)}
					dialogTitle="Add Bank Account"
					dialogSize={"sm"}
					isFullWidth={true}
				>
					{achClientSecret ? (
						<>
							<Box id="ach-embedded-checkout" sx={{ minHeight: 400 }}>
								<EmbeddedCheckoutProvider
									stripe={stripePromise}
									options={{ clientSecret: achClientSecret, onComplete: handleAchCheckoutComplete }}
								>
									<EmbeddedCheckout />
								</EmbeddedCheckoutProvider>
							</Box>
							{achSetupCompleted && (
								<Box sx={{ textAlign: 'right', mt: 2 }}>
									<LoadingButton
										type="button"
										variant='contained'
										loading={isConfirmingAch}
										onClick={handleAchCheckoutComplete}
									>
										<span>Done</span>
									</LoadingButton>
								</Box>
							)}
						</>
					) : (
						<Box sx={{ textAlign: 'center', py: 6 }}>
							<Typography variant='body2'>Loading bank verification…</Typography>
						</Box>
					)}
				</DialogTeleport>

				{/* Add amount to bank dialog */}
				<DialogTeleport
					dialogOpen={openAddAmount}
					handleDialogClose={() => setOpenAddAmount(false)}
					dialogTitle="Add Funds"
					dialogSize={"sm"}
					isFullWidth={true}
				>
					<form onSubmit={addAmountForm.handleSubmit}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<TextField
									data-cy='amount'
									id='amount'
									name="amount"
									label="Reward Deposit Amount"
									placeholder="Enter Amount"
									variant="outlined"
									fullWidth
									required
									// value={addAmountForm.values.amount}
									// onChange={addAmountForm.handleChange}
									value={walletMoney}
									onChange={(event) => {
										setWalletMoney(parseFloat(event.target.value).toFixed(2));
										if (ach_fee_type === 'percentage') {
											const fee = (parseFloat(event.target.value) * ach_fee) / 100;
											const total = parseFloat(event.target.value) + fee;
											setTotalWalletMoney(total || 0);
										} else if (ach_fee_type === 'fixed') {
											const total = parseFloat(event.target.value) + ach_fee;
											setTotalWalletMoney(total || 0);
										} else {
											setTotalWalletMoney(parseFloat(event.target.value));
										}
									}}
									// error={addAmountForm.touched.amount && Boolean(addAmountForm.errors.amount)}
									// helperText={addAmountForm.touched.amount && addAmountForm.errors.amount}
									margin='normal'
									InputProps={{
										inputComponent: MoneyInput,
									}}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</Grid>
							<Grid item xs={12}>
								<Grid container mt={3}>
									<Grid item xs={5}>
										<Typography variant='h6' component='h6' fontWeight={500} textAlign={"end"} >
											Transaction Fee:
										</Typography>
									</Grid>
									<Grid item xs={7}>
										<Typography variant='h6' color={'#000000'} component='h6' fontWeight={600} textAlign={"end"} >
											{
												ach_fee_type === 'percentage' 
												? `${ach_fee}%` 
												: ach_fee_type === 'fixed' 
													? `$${ach_fee.toFixed(2)}` 
													: '$0.00'
											} 
										</Typography>
									</Grid>
			
									<Grid item xs={5}>
										<Typography variant='h6' component='h6' fontWeight={500} textAlign={"end"} >
											Transaction Total:
										</Typography>
									</Grid>
									<Grid item xs={7}>
										<Typography variant='h6' color={'#000000'} component='h6' fontWeight={600} textAlign={"end"}>
											{`$${totalWalletMoney.toFixed(2)}`}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12}>
								<Box textAlign={'end'}>
									<LoadingButton
											type="submit"
											loading={loading.addAmountLoading}
											autoFocus
											variant='contained'
									>
										<span>Add Funds</span>
									</LoadingButton>
								</Box>
							</Grid>
						</Grid>
					</form>
				</DialogTeleport>
			</>
    )
}

export default ManageAddBankAccount